import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDisclosure } from '@heroui/react';
import { CourseBreadcrumbs } from '@/shared/ui/CourseBreadcrumbs';
import {
  useGetTeacherModuleAttendanceQuery,
  type ITeacherModuleAttendanceLesson,
  type ITeacherModuleAttendanceStudent,
} from '@/entities/TeacherAttendance';
import { CustomInput } from '@/shared/ui/Fields';
import { Svg } from '@/shared/ui/Svg';
import { useDebounce } from '@/shared/lib/hooks/useDebounce';
import { useAdminAccess } from '@/shared/lib/adminAccess';
import { routerPath } from '@/shared/config/router';
import SearchIcon from '@/shared/assets/icons/search.svg';
import { SectionLoader } from '@/shared/ui/Loader';
import { transformModuleAttendanceForTable } from '../lib/transformModuleAttendanceForTable';
import { parseModuleAttendanceRouteParams } from '../lib/utils';
import { ModuleAttendanceTable } from './components/ModuleAttendanceTable';
import { ChangeAttendanceModal } from './components/ChangeAttendanceModal';

export const TeacherModuleAttendanceView = () => {
  const { courseId, intakeId, groupNumber, termId, moduleId } = useParams<{
    courseId?: string;
    intakeId?: string;
    groupNumber?: string;
    termId?: string;
    moduleId?: string;
  }>();

  const { canEntity } = useAdminAccess();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search.trim(), 500);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [modalRecord, setModalRecord] = useState<{
    student: ITeacherModuleAttendanceStudent;
    lesson: ITeacherModuleAttendanceLesson;
    value: number | null;
  } | null>(null);

  const { params, skip } = parseModuleAttendanceRouteParams(moduleId, groupNumber);
  const { data, isLoading, isError, refetch } = useGetTeacherModuleAttendanceQuery(params, {
    skip,
  });
  const course = data?.course;
  const intake = data?.intake;
  const group = data?.group;
  const term = data?.term;
  const module = data?.module;

  const { students, valueMap, lessons } = useMemo(
    () => transformModuleAttendanceForTable(data),
    [data],
  );

  const filteredStudents = useMemo(() => {
    if (!debouncedSearch) return students;
    const s = debouncedSearch.toLowerCase();
    return students.filter(
      (st) =>
        st.username.toLowerCase().includes(s) ||
        st.email.toLowerCase().includes(s) ||
        (st.courseIdentifier?.toLowerCase().includes(s) ?? false) ||
        (st.firstName?.toLowerCase().includes(s) ?? false) ||
        (st.lastName?.toLowerCase().includes(s) ?? false),
    );
  }, [students, debouncedSearch]);

  const openChangeModal = (
    student: ITeacherModuleAttendanceStudent,
    lesson: ITeacherModuleAttendanceLesson,
  ) => {
    const val = valueMap.get(`${student.enrollmentId}-${lesson.groupLessonId}`);
    // val undefined = нет записи, показываем 0.0 по умолчанию
    setModalRecord({
      student,
      lesson,
      value: val !== undefined ? val : null,
    });
    onOpen();
  };

  const handleModalSuccess = () => {
    refetch();
  };

  const breadcrumbs = useMemo(() => {
    if (!course || !intake || !group || !term || !module) return [];
    const cId = (courseId ?? course.id).toString();
    const iId = (intakeId ?? intake.id).toString();
    const gNum = (groupNumber ?? group.number).toString();
    const tId = (termId ?? term.id).toString();
    return [
      { label: 'Courses', href: routerPath.courses },
      { label: course.title, href: routerPath.courseDetail.replace(':id', cId) },
      {
        label: `${intake.title} - Group ${gNum}`,
        href: routerPath.groupDetail
          .replace(':courseId', cId)
          .replace(':intakeId', iId)
          .replace(':groupNumber', gNum),
      },
      {
        label: `Term ${term.number}`,
        href: routerPath.termDetail
          .replace(':courseId', cId)
          .replace(':intakeId', iId)
          .replace(':groupNumber', gNum)
          .replace(':termId', tId),
      },
      { label: module.title },
      { label: 'Attendance' },
    ];
  }, [course, intake, group, term, module, courseId, intakeId, groupNumber, termId]);

  return (
    <div className="h-full min-h-0 flex flex-col gap-4 bg-white rounded-[24px] p-6 overflow-hidden">

      {breadcrumbs.length > 0 && <CourseBreadcrumbs items={breadcrumbs} />}

      <h2 className="text-[32px] font-medium">
        Attendance &#34;{module?.title ?? 'Module'}&#34;
      </h2>

      <div className="max-w-[530px]">
        <CustomInput
          startContent={<Svg Icon={SearchIcon} />}
          placeholder="Search"
          value={search}
          onValueChange={setSearch}
        />
      </div>

      <div className="flex gap-4 items-center text-sm">
        <span className="text-grey-secondary">Legend:</span>
        <span className="flex items-center gap-2">
          <span className="w-6 h-6 rounded bg-[#CFF7D3] text-black flex items-center justify-center text-xs font-medium">
            1.0
          </span>
          attended
        </span>
        <span className="flex items-center gap-2">
          <span className="w-6 h-6 rounded bg-[#FFF9C4] text-black flex items-center justify-center text-xs font-medium">
            0.5
          </span>
          lately joined or early left
        </span>
        <span className="flex items-center gap-2">
          <span className="w-6 h-6 rounded bg-[#FDD3D0] text-black flex items-center justify-center text-xs font-medium">
            0.0
          </span>
          lately joined or early left
        </span>
      </div>

      {isLoading ? (
        <SectionLoader />
      ) : isError ? (
        <div className="bg-white p-6 rounded-[24px]">
          <div className="text-red-tertiary text-center py-8">
            Error loading attendance. Please try again later.
          </div>
        </div>
      ) : !lessons.length || !filteredStudents.length ? (
        <div className="bg-white p-6 rounded-[24px]">
          <div className="text-center text-grey-secondary py-8">
            No attendance data available
          </div>
        </div>
      ) : (
        <ModuleAttendanceTable
          lessons={lessons}
          students={filteredStudents}
          valueMap={valueMap}
          onChangeAttendance={openChangeModal}
          canCreateAttendance={canEntity('attendance', 'create')}
          canUpdateAttendance={canEntity('attendance', 'update')}
        />

      )}

      {modalRecord && (
        <ChangeAttendanceModal
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          student={modalRecord.student}
          lesson={modalRecord.lesson}
          currentValue={modalRecord.value}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};
